import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslate } from 'src/locales';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import dayjs from 'dayjs';

interface OtherDetailProps {
  onClose: () => void;
  srnId?: number;
  udId?: number;
}

const OtherDetail: React.FC<OtherDetailProps> = ({ onClose, srnId, udId }) => {
  const { t } = useTranslate();

  console.log('[OtherDetail] Props:', { srnId, udId });

  // API isteği - useBaseQuery doğrudan response.data döndürür
  const hasValidIds = srnId !== undefined && srnId !== null && udId !== undefined && udId !== null;

  const { data: apiData, isLoading } = useBaseQuery<any>({
    endpoint: hasValidIds
      ? `/nurse/api/Order/GetOtherTreatmentsDetail?srnId=${srnId}&udId=${udId}`
      : '',
    queryKey: ['other-treatment-detail', srnId, udId],
    options: {
      enabled: hasValidIds,
      staleTime: 0,
    },
  });

  console.log('[OtherDetail] API Response:', { apiData, isLoading, hasValidIds });

  // API doğrudan obje veya dizi döndürebilir
  const detailData = Array.isArray(apiData) ? apiData[0] : apiData;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, width: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '533px',
        boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.25)',
        borderRadius: '20px',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'flex-start',
        p: 1.875,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1.875,
        }}
      >
        {/* Başlık (Category / Type) */}
        <Box
          sx={{
            minHeight: 39,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: -0.01,
              lineHeight: 1.4,
            }}
          >
            {detailData?.name || detailData?.srntype || '-'}
          </Typography>
        </Box>

        {/* Genel Bilgiler */}
        <Box
          sx={{
            width: '100%',
            boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
            borderRadius: '20px',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 1.25,
            gap: 0.375,
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2.5,
              py: 1.15,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: -0.01,
                lineHeight: '22px',
              }}
            >
              {t('nurse.order-medicine.detail.general-info')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#767a85',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              {/* Plan Saati */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.plan-time')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.startdate ? dayjs(detailData.startdate).format('HH:mm') : '-'}
                </Typography>
              </Box>

              {/* Başlangıç Tarihi */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.start-time')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.startdate ? dayjs(detailData.startdate).format('DD.MM.YYYY') : '-'}
                </Typography>
              </Box>

              {/* Bitiş Tarihi */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.end-time')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.finishdate ? dayjs(detailData.finishdate).format('DD.MM.YYYY') : '-'}
                </Typography>
              </Box>

              {/* Miktar */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.amount')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.quantity ?? '-'}
                </Typography>
              </Box>

              {/* Kan Grubu */}
              {detailData?.isblood === '1' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.blood-group')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    {detailData?.bloodgroupname || '-'}
                  </Typography>
                </Box>
              )}

              {/* SRN Tipi */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.treatment-type')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.srntype || '-'}
                </Typography>
              </Box>

              {/* Yapıldı mı */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.done')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.isdone === '1' ? 'Evet' : 'Hayır'}
                </Typography>
              </Box>

              {/* İptal */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.cancel')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.iscancel === '1' ? 'Evet' : 'Hayır'}
                </Typography>
              </Box>

              {/* Kan Ürünü mü */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.is-blood')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.isblood === '1' ? 'Evet' : 'Hayır'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Notlar */}
        <Box
          sx={{
            width: '100%',
            boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
            borderRadius: '20px',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 1.25,
            gap: 0.375,
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2.5,
              py: 1.15,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: -0.01,
                lineHeight: '22px',
              }}
            >
              {t('nurse.order-medicine.detail.notes')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#767a85',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              {/* Açıklama / İsim */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.description')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.name || '-'}
                </Typography>
              </Box>

              {/* Yapıldı mı */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.done')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.isdone === '1' ? 'Evet' : 'Hayır'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Süre / Onay */}
        <Box
          sx={{
            width: '100%',
            boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
            borderRadius: '20px',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 1.25,
            gap: 0.375,
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2.5,
              py: 1.15,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: -0.01,
                lineHeight: '22px',
              }}
            >
              {t('nurse.order-medicine.detail.duration-approval')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#767a85',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 2.5,
                }}
              >
                {/* Uygulama Tarihi */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.application-date')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    {detailData?.appdate ? dayjs(detailData.appdate).format('DD.MM.YYYY') : '-'}
                  </Typography>
                </Box>

                {/* Uygulama Saati */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.application-time-full')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    {detailData?.appdate ? dayjs(detailData.appdate).format('HH:mm') : '-'}
                  </Typography>
                </Box>

                {/* İşlemi Yapan */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.processor')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    -
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OtherDetail;
